// src/screens/ModMatch.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { LogoLoader } from "../components/LogoLoader";
import { CalendarDays, Clock, Save, ArrowLeft } from "lucide-react";
import i18n from "../i18n";

function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      String((i18n.t as any)(key, options)),
    []
  );
  return { t, lng };
}

type Match = {
  id: string;
  user_id?: string;
  opponent: string;
  date: string;
};

/** Extrait "YYYY-MM-DD" et "HH:MM" d'une valeur timestamp sans décalage de fuseau. */
function splitDateTime(value: string | null | undefined): { date: string; time: string } {
  if (!value) return { date: "", time: "" };
  const m = String(value).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (m) return { date: m[1], time: m[2] };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export const ModMatch = () => {
  const { lng } = useI18nSafe();
  const { id: matchId } = useParams();
  const navigate = useNavigate();

  const L = (fr: string, en: string) => (lng === "fr" ? fr : en);

  const [match, setMatch] = useState<Match | null>(null);
  const [opponent, setOpponent] = useState("");
  const [date, setDate] = useState(""); // "YYYY-MM-DD"
  const [time, setTime] = useState(""); // "HH:MM"
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const opponentLabel = useMemo(() => match?.opponent ?? "", [match]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) console.error(userError);
        if (!user) {
          navigate("/login");
          return;
        }

        const { data: matchData, error: matchError } = await supabase
          .from("matches")
          .select("id, user_id, opponent, date")
          .eq("id", matchId)
          .eq("user_id", user.id)
          .single();

        if (matchError || !matchData) {
          console.error("Load error:", matchError);
          alert(L("Erreur de chargement du match.", "Error loading the game."));
          navigate(`/matchdetails/${matchId}`);
          return;
        }

        if (!isMounted) return;
        setMatch(matchData as Match);
        setOpponent(matchData.opponent ?? "");
        const { date: d, time: h } = splitDateTime(matchData.date);
        setDate(d);
        setTime(h);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, navigate]);

  const handleSubmit = async () => {
    if (!matchId) return;

    if (!opponent.trim() || !date || !time) {
      alert(L("Merci de remplir l'adversaire, la date et l'heure.", "Please fill in opponent, date and time."));
      return;
    }

    setSaving(true);
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) console.error(userError);
      if (!user) {
        navigate("/login");
        return;
      }

      const dateTime = `${date}T${time.length === 5 ? `${time}:00` : time}`;

      const { data, error } = await supabase
        .from("matches")
        .update({
          opponent: opponent.trim(),
          date: dateTime,
        })
        .eq("id", matchId)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error || !data) {
        console.error("Erreur mise à jour :", error);
        alert(L("Erreur lors de la mise à jour du match.", "Error updating the game."));
        return;
      }

      alert(L("Match mis à jour !", "Game updated!"));
      navigate(`/matchdetails/${matchId}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !match) {
    return <LogoLoader />;
  }

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link
            to={`/matchdetails/${matchId}`}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {L("Retour", "Back")}
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-center mb-6 text-orange-600 dark:text-orange-400">
          {L(`Modifier le match vs ${opponentLabel}`, `Edit game vs ${opponentLabel}`)}
        </h1>

        <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
          {/* Adversaire */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {L("Adversaire", "Opponent")}
            </label>
            <input
              type="text"
              value={opponent}
              onChange={(e) => setOpponent(e.target.value)}
              placeholder={L("Nom de l'équipe adverse", "Opponent team name")}
              className="w-full rounded-lg px-3 py-2 border
                         bg-white border-gray-300 text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
            />
          </div>

          {/* Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {L("Date", "Date")}
            </label>
            <div className="relative">
              <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full appearance-none text-sm rounded-lg pl-9 pr-3 py-2 border
                           bg-white border-gray-300 text-gray-900
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Heure */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {L("Heure", "Time")}
            </label>
            <div className="relative">
              <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                step={60}
                className="w-full appearance-none text-sm rounded-lg pl-9 pr-3 py-2 border
                           bg-white border-gray-300 text-gray-900
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Sauvegarde */}
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`inline-flex items-center gap-2 font-semibold py-3 px-6 rounded-lg transition
                        focus:outline-none focus:ring-2 focus:ring-orange-400/60
                        ${
                          saving
                            ? "bg-orange-300 text-white cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700 text-white"
                        }`}
            aria-busy={saving}
          >
            <Save className="w-4 h-4" />
            {saving
              ? L("Enregistrement…", "Saving…")
              : L("Enregistrer les modifications", "Save changes")}
          </button>
        </div>
      </div>
    </section>
  );
};
